import { useState, useEffect } from 'react'
import { LabelFilters as LabelFiltersType } from '../../types/label'
import { useGenres, useStyles, useCountries } from '../../hooks'
import { Button } from '../common'
import styles from '../releases/Releases.module.css'

interface LabelFiltersProps {
  filters: LabelFiltersType
  onFiltersChange: (filters: LabelFiltersType) => void
  onClose?: () => void
}

export function LabelFilters({ filters, onFiltersChange, onClose }: LabelFiltersProps) {
  const { data: genresData } = useGenres()
  const { data: stylesData } = useStyles()
  const { data: countriesData } = useCountries()

  const [genreSearch, setGenreSearch] = useState('')
  const [styleSearch, setStyleSearch] = useState('')
  const [countrySearch, setCountrySearch] = useState('')
  const [genreMode, setGenreMode] = useState<'or' | 'and'>(filters.genre_mode || 'or')
  const [styleMode, setStyleMode] = useState<'or' | 'and'>(filters.style_mode || 'or')
  const [minScore, setMinScore] = useState('')
  const [minMatchScore, setMinMatchScore] = useState('')
  const [minReleaseCount, setMinReleaseCount] = useState('')
  const [minWant, setMinWant] = useState('')
  const [yearFrom, setYearFrom] = useState(filters.year_from?.toString() || '')
  const [yearTo, setYearTo] = useState(filters.year_to?.toString() || '')
  const [selectedGenres, setSelectedGenres] = useState<string[]>(filters.genres || [])
  const [selectedStyles, setSelectedStyles] = useState<string[]>(filters.styles || [])
  const [selectedCountries, setSelectedCountries] = useState<string[]>(filters.countries || [])

  useEffect(() => {
    setYearFrom(filters.year_from?.toString() || '')
    setYearTo(filters.year_to?.toString() || '')
    setSelectedGenres(filters.genres || [])
    setSelectedStyles(filters.styles || [])
    setSelectedCountries(filters.countries || [])
    setGenreMode(filters.genre_mode || 'or')
    setStyleMode(filters.style_mode || 'or')
  }, [filters])

  const handleApplyFilters = () => {
    onFiltersChange({
      ...filters,
      min_elevated_score: minScore ? parseFloat(minScore) : undefined,
      min_match_score: minMatchScore ? parseFloat(minMatchScore) : undefined,
      min_release_count: minReleaseCount ? parseInt(minReleaseCount) : undefined,
      min_want: minWant ? parseInt(minWant) : undefined,
      year_from: yearFrom ? parseInt(yearFrom) : undefined,
      year_to: yearTo ? parseInt(yearTo) : undefined,
      genres: selectedGenres.length > 0 ? selectedGenres : undefined,
      styles: selectedStyles.length > 0 ? selectedStyles : undefined,
      countries: selectedCountries.length > 0 ? selectedCountries : undefined,
      genre_mode: genreMode,
      style_mode: styleMode,
      page: 1,
    })
    onClose?.()
  }

  const handleReset = () => {
    setMinScore('')
    setMinMatchScore('')
    setMinReleaseCount('')
    setMinWant('')
    setYearFrom('')
    setYearTo('')
    setSelectedGenres([])
    setSelectedStyles([])
    setSelectedCountries([])
    setGenreSearch('')
    setStyleSearch('')
    setCountrySearch('')
    onFiltersChange({
      page: 1,
      per_page: filters.per_page,
      sort: filters.sort,
      order: filters.order,
      q: filters.q
    })
    onClose?.()
  }

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    )
  }

  const toggleStyle = (style: string) => {
    setSelectedStyles(prev =>
      prev.includes(style) ? prev.filter(s => s !== style) : [...prev, style]
    )
  }

  const toggleCountry = (country: string) => {
    setSelectedCountries(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    )
  }

  const filteredGenres = genresData?.genres?.filter(g =>
    g.name.toLowerCase().includes(genreSearch.toLowerCase())
  ) || []

  const filteredStyles = stylesData?.styles?.filter(s =>
    s.name.toLowerCase().includes(styleSearch.toLowerCase())
  ) || []

  const filteredCountries = countriesData?.countries?.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  ) || []

  return (
    <div className={styles.filterPanel}>
      <div className={styles.filterMobileHeader}>
        <div>
          <span className={styles.filterMobileEyebrow}>Refine labels</span>
          <h2>Filters</h2>
        </div>
        {onClose && (
          <button className={styles.filterMobileClose} onClick={onClose} aria-label="Close filters">
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>
      {/* Genre / Style / Country columns */}
      <div className={styles.filterColumns}>
        <div className={styles.filterColumn}>
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-music-note-beamed"></i> Genres
            </span>
            <div className={styles.filterModeToggle}>
              <button
                className={`${styles.filterModeBtn} ${genreMode === 'or' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setGenreMode('or')}
                title="Match any selected genre (OR)"
              >
                <i className="bi bi-union"></i>
              </button>
              <button
                className={`${styles.filterModeBtn} ${genreMode === 'and' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setGenreMode('and')}
                title="Match all selected genres (AND)"
              >
                <i className="bi bi-intersect"></i>
              </button>
            </div>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Genres"
                value={genreSearch}
                onChange={(e) => setGenreSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {filteredGenres.map(genre => (
              <label key={genre.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedGenres.includes(genre.name)}
                  onChange={() => toggleGenre(genre.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedGenres.includes(genre.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {genre.name}
                  <span className={styles.checkboxCount}>({genre.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.filterColumn}>
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-gear"></i> Styles
            </span>
            <div className={styles.filterModeToggle}>
              <button
                className={`${styles.filterModeBtn} ${styleMode === 'or' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setStyleMode('or')}
                title="Match any selected style (OR)"
              >
                <i className="bi bi-union"></i>
              </button>
              <button
                className={`${styles.filterModeBtn} ${styleMode === 'and' ? styles.filterModeBtnActive : ''}`}
                onClick={() => setStyleMode('and')}
                title="Match all selected styles (AND)"
              >
                <i className="bi bi-intersect"></i>
              </button>
            </div>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Styles"
                value={styleSearch}
                onChange={(e) => setStyleSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {filteredStyles.map(style => (
              <label key={style.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedStyles.includes(style.name)}
                  onChange={() => toggleStyle(style.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedStyles.includes(style.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {style.name}
                  <span className={styles.checkboxCount}>({style.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.filterColumn}>
          <div className={styles.filterColumnHeader}>
            <span className={styles.filterColumnLabel}>
              <i className="bi bi-globe"></i> Countries
            </span>
            <div className={styles.filterColumnSearch}>
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search Countries"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.filterColumnList}>
            {filteredCountries.map(country => (
              <label key={country.name} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  checked={selectedCountries.includes(country.name)}
                  onChange={() => toggleCountry(country.name)}
                />
                <span className={styles.checkboxCustom} data-checked={selectedCountries.includes(country.name) ? '' : undefined} />
                <span className={styles.checkboxLabel}>
                  {country.name}
                  <span className={styles.checkboxCount}>({country.count.toLocaleString()})</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Numeric Filters */}
      <div className={styles.filterGrid}>
        <div className={styles.filterGroup}>
          <label>Min Gem Score</label>
          <input
            type="number"
            step="0.1"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            placeholder="e.g. 0.5"
          />
        </div>
        <div className={styles.filterGroup}>
          <label>Min Match Score</label>
          <input
            type="number"
            step="0.1"
            value={minMatchScore}
            onChange={(e) => setMinMatchScore(e.target.value)}
            placeholder="e.g. 0.5"
          />
        </div>
        <div className={styles.filterGroup}>
          <label>Min Release Count</label>
          <input
            type="number"
            value={minReleaseCount}
            onChange={(e) => setMinReleaseCount(e.target.value)}
            placeholder="e.g. 10"
          />
        </div>
        <div className={styles.filterGroup}>
          <label>Min Want</label>
          <input
            type="number"
            value={minWant}
            onChange={(e) => setMinWant(e.target.value)}
            placeholder="e.g. 100"
          />
        </div>
        <div className={styles.filterGroup}>
          <label>Year From</label>
          <input type="number" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="e.g. 1990" />
        </div>
        <div className={styles.filterGroup}>
          <label>Year To</label>
          <input type="number" value={yearTo} onChange={(e) => setYearTo(e.target.value)} placeholder="e.g. 2000" />
        </div>
      </div>

      <div className={styles.filterActions}>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="secondary" onClick={handleReset}>
          Reset
        </Button>
      </div>
    </div>
  )
}
